import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { List, Plus, Pencil, Trash2, ChevronRight } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import {
  getCollections, createCollection, updateCollection, deleteCollection,
  getCollectionItems, addCollectionItem, updateCollectionItem, removeCollectionItem,
} from '../api/brainer'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BCollection, BCollectionItem } from '../types'

export function BrainerCollections() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const { data: collections = [], isLoading } = useQuery({
    queryKey: ['b-collections'],
    queryFn: () => getCollections().then(r => r.data),
  })

  // Create
  const [showCreate, setShowCreate] = useState(false)
  const [cForm, setCForm] = useState({ name: '', description: '' })
  const create = useMutation({
    mutationFn: () => createCollection({ name: cForm.name, description: cForm.description || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-collections'] }); setShowCreate(false); setCForm({ name: '', description: '' }) },
  })

  // Edit
  const [editItem, setEditItem] = useState<BCollection | null>(null)
  const [eForm, setEForm] = useState({ name: '', description: '' })
  const openEdit = (c: BCollection) => { setEditItem(c); setEForm({ name: c.name, description: c.description ?? '' }) }
  const save = useMutation({
    mutationFn: () => updateCollection(editItem!.id, { name: eForm.name, description: eForm.description || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-collections'] }); setEditItem(null) },
  })

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<BCollection | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteCollection(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-collections'] }); setDeleteTarget(null) },
  })

  // Items panel
  const [itemsFor, setItemsFor] = useState<BCollection | null>(null)

  const [search, setSearch] = useState('')
  const filtered = (collections as BCollection[]).filter(c =>
    [c.name, c.description ?? ''].some(v => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Collections</h1>
          <p className="text-sm text-ink-dim mt-0.5">Named lists of choices — used by Array-type traits</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search collections" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New collection</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : collections.length === 0 ? (
          <EmptyState icon={List} title="No collections" description="Create a collection to define choices for Array-type traits" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c: BCollection) => (
                <tr key={c.id}>
                  <td>
                    <button onClick={() => setItemsFor(c)} className="font-medium text-ink hover:text-accent transition-colors text-left">
                      {c.name}
                    </button>
                  </td>
                  <td className="text-ink-dim text-xs">{c.description ?? ''}</td>
                  <td className="text-ink-dim">{fmt(c.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => setItemsFor(c)} className="flex items-center gap-1 text-xs text-ink-dim hover:text-ink font-medium">
                        <ChevronRight size={13} /> Items
                      </button>
                      <button onClick={() => openEdit(c)} className="text-ink-dim hover:text-ink transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => setDeleteTarget(c)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New collection" onClose={() => setShowCreate(false)} size="sm">
          <CollectionForm form={cForm} onChange={setCForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!cForm.name || create.isPending} loading={create.isPending}>
              Create
            </Button>
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {editItem && (
        <Modal title={`Edit — ${editItem.name}`} onClose={() => setEditItem(null)} size="sm">
          <CollectionForm form={eForm} onChange={setEForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!eForm.name || save.isPending} loading={save.isPending}>
              Save
            </Button>
          </div>
        </Modal>
      )}

      {/* Items modal */}
      {itemsFor && (
        <ItemsModal collection={itemsFor} onClose={() => setItemsFor(null)} />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete collection"
          message={`Delete "${deleteTarget.name}"? All items and trait associations will be removed.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function CollectionForm({ form, onChange }: { form: { name: string; description: string }; onChange: (f: any) => void }) {
  return (
    <div className="space-y-4">
      <Field label="Name *">
        <input value={form.name} onChange={e => onChange((f: any) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. ID card types" className="field" autoFocus />
      </Field>
      <Field label="Description">
        <input value={form.description} onChange={e => onChange((f: any) => ({ ...f, description: e.target.value }))}
          placeholder="Optional" className="field" />
      </Field>
    </div>
  )
}

function ItemsModal({ collection, onClose }: { collection: BCollection; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['b-collection-items', collection.id],
    queryFn: () => getCollectionItems(collection.id).then(r => r.data),
  })

  const [addValue, setAddValue] = useState('')
  const add = useMutation({
    mutationFn: () => addCollectionItem(collection.id, { value: addValue, position: (items as BCollectionItem[]).length }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-collection-items', collection.id] }); setAddValue('') },
  })

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const update = useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      updateCollectionItem(id, { value: editValue, position }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-collection-items', collection.id] }); setEditingId(null) },
  })

  const remove = useMutation({
    mutationFn: (id: string) => removeCollectionItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-collection-items', collection.id] }),
  })

  function startEdit(item: BCollectionItem) {
    setEditingId(item.id)
    setEditValue(item.value)
  }

  return (
    <Modal title={`Items — ${collection.name}`} onClose={onClose} size="md">
      <div className="space-y-4">
        {/* Add item */}
        <div className="flex gap-2">
          <input
            value={addValue}
            onChange={e => setAddValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && addValue.trim()) add.mutate() }}
            placeholder="New item value…"
            className="field flex-1"
          />
          <Button icon={Plus} onClick={() => add.mutate()} disabled={!addValue.trim() || add.isPending}>Add</Button>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="py-6 text-center text-sm text-ink-dim">Loading…</div>
        ) : (items as BCollectionItem[]).length === 0 ? (
          <p className="text-sm text-ink-dim text-center py-4">No items yet</p>
        ) : (
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {(items as BCollectionItem[]).map((item, idx) => (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-paper-border">
                <span className="text-xs text-ink-dim font-mono w-5 text-right shrink-0">{idx + 1}</span>
                {editingId === item.id ? (
                  <input
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') update.mutate({ id: item.id, position: item.position })
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                    className="field flex-1 text-xs py-1"
                    autoFocus
                  />
                ) : (
                  <span className="flex-1 text-sm text-ink">{item.value}</span>
                )}
                {editingId === item.id ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => update.mutate({ id: item.id, position: item.position })}
                      disabled={update.isPending}
                    >
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(item)} className="text-ink-dim hover:text-ink transition-colors"><Pencil size={12} /></button>
                    <button onClick={() => remove.mutate(item.id)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-paper-border">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  )
}
